/**
 * What the user is looking at on the Workflow Builder canvas, published for
 * the agent.
 *
 * The agent could already COMPOSE a workflow (backend bridge kinds
 * data_workflow / inspect_workflow_sources), but it could not SEE the one on
 * screen — AgentOverlay's page context carried four route ids and nothing
 * about the graph. So "why is this join empty?" had no referent.
 *
 * A module-level reactive store rather than props: the canvas and the agent
 * overlay live in different branches of the tree (the overlay is mounted by
 * the top nav), so threading state between them would mean plumbing through
 * every intermediate component for one consumer.
 *
 * DESIGN NOTE — this publishes SHAPE, NOT DATA. Node names, roles, column
 * names and join keys: the vocabulary needed to reason about a graph. No
 * rows, no values, nothing patient-identifying. The agent asks for rows
 * through the governed preview endpoint when it needs them, which keeps
 * tenant scoping in one place instead of two.
 */
import { reactive } from 'vue';

export const canvasContext = reactive({
  active: false,   // is a canvas on screen right now?
  workflow: null,  // { id, name, status } of the workflow being edited
  nodes: [],       // [{ id, da_id, name, role, columns: [] }]
  edges: [],       // [{ source, target, step_op, join_type, join_key }]
  unjoined: [],    // names excluded from the compiled config
});

/** Called by the canvas whenever its graph changes. */
export function publishCanvas(snapshot) {
  canvasContext.active = true;
  canvasContext.workflow = snapshot.workflow || null;
  canvasContext.nodes = snapshot.nodes || [];
  canvasContext.edges = snapshot.edges || [];
  canvasContext.unjoined = snapshot.unjoined || [];
}

/** Called when the canvas unmounts, so the agent stops describing a screen
 *  the user has navigated away from. */
export function clearCanvas() {
  canvasContext.active = false;
  canvasContext.workflow = null;
  canvasContext.nodes = [];
  canvasContext.edges = [];
  canvasContext.unjoined = [];
}

/**
 * The canvas slice of the agent's page context, or null when no canvas is up.
 *
 * Deliberately small and stable: this is stamped and diffed on every route
 * change (AgentOverlay.tellPage), so a payload that churns would spam the
 * session with context turns that say nothing new.
 */
export function canvasPageContext() {
  if (!canvasContext.active) return null;
  return {
    workflow: canvasContext.workflow,
    nodes: canvasContext.nodes.map(n => ({
      name: n.name, role: n.role, da_id: n.da_id, columns: n.columns || [],
    })),
    joins: canvasContext.edges.map(e => ({
      from: e.sourceName, to: e.targetName,
      op: e.step_op || 'join',
      join_type: e.join_type || null,
      on: e.join_key || null,
    })),
    unjoined: canvasContext.unjoined,
  };
}
